import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { User, UserRole } from '../types';

// Helper pour ajouter un timeout aux promesses
const withTimeout = <T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> => {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), ms);
  });
  return Promise.race([promise, timeout]);
};

export const authService = {
  async login(email: string, password: string): Promise<SupabaseUser> {
    // Timeout de 10 secondes pour le login
    const loginPromise = supabase.auth.signInWithPassword({
      email,
      password
    });

    const { data, error } = await withTimeout(
      loginPromise,
      10000,
      'La connexion prend trop de temps. Vérifiez votre connexion internet.'
    );

    if (error) {
      console.error('Login error:', error);
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Email ou mot de passe incorrect');
      }
      throw new Error('Échec de la connexion');
    }

    if (!data.user) {
      throw new Error('Échec de la connexion');
    }

    // Update last login time (sans attendre, non bloquant)
    supabase
      .from('users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', data.user.id)
      .then(() => undefined);

    return data.user;
  },

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Logout error:', error);
      throw new Error('Échec de la déconnexion');
    }
  },

  onAuthStateChange(callback: (user: SupabaseUser | null) => void) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const user = session?.user ?? null;

        if (user && event === 'SIGNED_IN') {
          // Try to create a default user profile if it doesn't exist
          // Uses insert with try-catch to handle race conditions
          // (usersService.create() might have already created the profile with the correct role/org)
          try {
            const { error } = await supabase.from('users').insert({
              id: user.id,
              email: user.email || '',
              role: 'client',
              created_at: new Date().toISOString(),
              last_login_at: new Date().toISOString()
            });
            // Ignore duplicate key errors (23505) - profile already exists
            if (error && error.code !== '23505') {
              console.error('Error creating default user profile:', error);
            }
          } catch (err) {
            // Silently ignore - profile might already exist
          }
        }

        callback(user);
      }
    );

    return () => subscription.unsubscribe();
  },

  async getCurrentUser(): Promise<SupabaseUser | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  async isAuthenticated(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  },

  async getUserProfile(userId: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      email: data.email,
      role: data.role as UserRole,
      organizationId: data.organization_id,
      displayName: data.display_name,
      avatarUrl: data.avatar_url,
      lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
      createdAt: new Date(data.created_at)
    };
  },

  async getCurrentUserProfile(): Promise<User | null> {
    const user = await this.getCurrentUser();
    if (!user) return null;
    return this.getUserProfile(user.id);
  },

  async isSuperAdmin(): Promise<boolean> {
    const profile = await this.getCurrentUserProfile();
    return profile?.role === 'super_admin';
  },

  async createUser(
    email: string,
    password: string,
    role: UserRole = 'client',
    organizationId?: string
  ): Promise<User> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password
    });

    if (error) {
      console.error('Create user error:', error);
      if (error.message.includes('already registered')) {
        throw new Error('Un compte existe déjà avec cet email');
      }
      throw new Error('Échec de la création du compte');
    }

    if (!data.user) {
      throw new Error('Échec de la création du compte');
    }

    const userData: User = {
      id: data.user.id,
      email: data.user.email || '',
      role,
      organizationId: organizationId || null,
      createdAt: new Date()
    };

    // Use upsert to handle race condition with onAuthStateChange
    await supabase.from('users').upsert({
      id: userData.id,
      email: userData.email,
      role: userData.role,
      organization_id: userData.organizationId,
      created_at: new Date().toISOString(),
      last_login_at: new Date().toISOString()
    }, { onConflict: 'id' });

    return userData;
  },

  async updateUserProfile(userId: string, updates: Partial<User>): Promise<void> {
    const updateData: Record<string, unknown> = {};

    if (updates.displayName !== undefined) updateData.display_name = updates.displayName;
    if (updates.avatarUrl !== undefined) updateData.avatar_url = updates.avatarUrl;
    if (updates.role !== undefined) updateData.role = updates.role;
    if (updates.organizationId !== undefined) updateData.organization_id = updates.organizationId;

    const { error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', userId);

    if (error) {
      console.error('Update profile error:', error);
      throw new Error('Impossible de mettre à jour le profil');
    }
  }
};
